// localStorage that never throws: private mode, blocked site data or a full
// quota fall back to an in-memory map for the rest of the session. Writes are
// announced to in-page listeners, and writes from other tabs (the "storage"
// event) are announced the same way, so settings stay in sync across tabs.

type Listener = (key: string | null) => void;

const memory = new Map<string, string>();
const listeners = new Set<Listener>();
let crossTabAttached = false;

const notify = (key: string | null) => listeners.forEach(listener => listener(key));

const attachCrossTab = () => {
    if (crossTabAttached || typeof window === 'undefined') {
        return;
    }
    crossTabAttached = true;
    window.addEventListener('storage', event => {
        // A null key means the whole storage was cleared.
        notify(event.key);
    });
};

// The memory map only holds values whose write to localStorage failed, so
// when it has one it is newer than whatever localStorage still contains.
export const safeGet = (key: string): string | null => {
    const remembered = memory.get(key);
    if (remembered !== undefined) {
        return remembered;
    }
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

export const safeSet = (key: string, value: string): void => {
    try {
        window.localStorage.setItem(key, value);
        memory.delete(key);
    } catch {
        memory.set(key, value);
    }
    notify(key);
};

export const safeRemove = (key: string): void => {
    try {
        window.localStorage.removeItem(key);
    } catch {
        // nothing to do
    }
    memory.delete(key);
    notify(key);
};

export const subscribeStorage = (listener: Listener): (() => void) => {
    attachCrossTab();
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// Session storage with the same never-throw behaviour, for guards that must
// not outlive the tab (such as "already reloaded for this version").
export const sessionGet = (key: string): string | null => {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return memory.get(`session:${key}`) ?? null;
    }
};

export const sessionSet = (key: string, value: string): void => {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        memory.set(`session:${key}`, value);
    }
};
